"use client";

import { useEffect, useState } from "react";
import { MessageCircle, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import listMentionableUsers from "@/new_actions/auth/list_mentionable_users";
import ChatPanel from "@/components/chat-panel";
import ThreadList, { THREAD_LABELS } from "@/components/chat/thread-list";
import type { ChatUser, ThreadType } from "@/types/chat";

interface MobileChatProps {
  currentUser: { id: string; username: string };
  className?: string;
}

/** Mobile chat: header button that opens a thread-list/chat sheet. */
export default function MobileChat({ currentUser, className }: MobileChatProps) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [activeThread, setActiveThread] = useState<{
    type: ThreadType;
    id: string;
  } | null>(null);

  // Load users once when sheet first opens
  useEffect(() => {
    if (!open || users.length > 0) return;
    (async () => {
      try {
        const rawUsers = await listMentionableUsers();
        const raw = rawUsers as Record<string, unknown>;
        const list = Array.isArray(raw?.users)
          ? raw.users
          : Array.isArray(rawUsers)
            ? rawUsers
            : [];
        setUsers(list as ChatUser[]);
      } catch {
        // silent
      }
    })();
  }, [open, users.length]);

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setActiveThread(null);
      }}
    >
      <SheetTrigger asChild>
        <button
          className={cn(
            "relative inline-flex size-9 items-center justify-center rounded-md transition-colors hover:bg-muted md:hidden",
            className,
          )}
          aria-label="Открыть чаты"
        >
          <MessageCircle className="size-4" />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
        {/* Header */}
        <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
          {activeThread && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setActiveThread(null)}
              aria-label="Назад к списку чатов"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <h3 className="flex-1 text-sm font-semibold">
            {activeThread
              ? (THREAD_LABELS[activeThread.type] ?? activeThread.type)
              : "Чаты"}
          </h3>
        </div>

        {/* Content */}
        {activeThread ? (
          <ChatPanel
            threadType={activeThread.type}
            threadId={activeThread.id}
            currentUser={currentUser}
            users={users}
            className="flex-1 rounded-none border-0"
            hideHeader
          />
        ) : (
          <ThreadList active={open && !activeThread} onSelect={setActiveThread} />
        )}
      </SheetContent>
    </Sheet>
  );
}
